import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { BarChart3 } from 'lucide-react';
import { LoginForm } from '@/components/auth/LoginForm';
import { RegisterForm } from '@/components/auth/RegisterForm';
import { ForgotPasswordForm } from '@/components/auth/ForgotPasswordForm';
import { UpdatePasswordForm } from '@/components/auth/UpdatePasswordForm';
import { useAuth } from '@/hooks/useAuth';

export const AuthPage: React.FC = () => {
  const [mode, setMode] = useState<'login' | 'signup' | 'forgot-password' | 'update-password'>('login');
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();

  // Check URL params for mode
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const modeParam = searchParams.get('mode');
    if (modeParam === 'signup') {
      setMode('signup');
    } else if (modeParam === 'login') {
      setMode('login');
    } else if (modeParam === 'forgot-password') {
      setMode('forgot-password');
    } else if (modeParam === 'update-password') {
      setMode('update-password');
    }
  }, [location.search]);

  // Redirect if already authenticated (SAUF en mode update-password)
  useEffect(() => {
    if (isAuthenticated && mode !== 'update-password') {
      const state = location.state as { from?: string };
      const searchParams = new URLSearchParams(location.search);
      const redirectParam = searchParams.get('redirect');
      const from = redirectParam || state?.from || '/dashboard';
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location.state, location.search, mode]);

  const handleSuccess = () => {
    // Redirect to the page they were trying to access or dashboard
    const state = location.state as { from?: string };
    const searchParams = new URLSearchParams(location.search);
    const redirectParam = searchParams.get('redirect');
    const from = redirectParam || state?.from || '/dashboard';
    navigate(from, { replace: true });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Helmet>
        <title>
          {mode === 'login' ? 'Connexion - DPEWizard' :
           mode === 'signup' ? 'Inscription - DPEWizard' :
           mode === 'forgot-password' ? 'Mot de passe oublié - DPEWizard' :
           mode === 'update-password' ? 'Nouveau mot de passe - DPEWizard' :
           'Authentification - DPEWizard'}
        </title>
        <meta name="description" content={
          mode === 'login' ? 'Connectez-vous à votre compte DPEWizard pour accéder à vos analyses DPE et recommandations personnalisées.' :
          mode === 'signup' ? 'Créez votre compte DPEWizard et commencez à analyser vos DPE avec notre IA experte.' :
          mode === 'forgot-password' ? 'Réinitialisez votre mot de passe DPEWizard en quelques clics.' :
          mode === 'update-password' ? 'Définissez votre nouveau mot de passe DPEWizard.' :
          'Authentification DPEWizard - Accédez à votre compte ou créez-en un nouveau.'
        } />
      </Helmet>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-green-600 rounded-lg flex items-center justify-center mb-4">
              <BarChart3 className="w-7 h-7 text-white" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">
            DPEWizard
          </h1>
          <p className="text-gray-600 mt-2">
            Analyse DPE intelligente avec IA
          </p>
        </div>

        {mode === 'login' ? (
          <LoginForm
            onSuccess={handleSuccess}
            onSwitchToSignup={() => setMode('signup')}
          />
        ) : mode === 'signup' ? (
          <RegisterForm
            onSuccess={handleSuccess}
            onSwitchToLogin={() => setMode('login')}
          />
        ) : mode === 'forgot-password' ? (
          <ForgotPasswordForm
            onSwitchToLogin={() => setMode('login')}
          />
        ) : mode === 'update-password' ? (
          <UpdatePasswordForm />
        ) : (
          <LoginForm
            onSuccess={handleSuccess}
            onSwitchToSignup={() => setMode('signup')}
          />
        )}
      </div>
    </div>
  );
};

export default AuthPage;